import { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useChatStore, Message, DebugEvent, ConsiliumTurn, nextTempId } from '../stores/chatStore'
import { useWebSocket } from '../hooks/useWebSocket'
import type { TypingMetrics } from '../hooks/useTypingMetrics'
import { useVoiceRecorder, blobToBase64 } from '../hooks/useVoiceRecorder'
import { useVideoRecorder } from '../hooks/useVideoRecorder'
import { uploadVideo } from '../api/media'
import { useAudioAnalyser } from '../hooks/useAudioAnalyser'
import { useDevMode } from '../hooks/useDevMode'
import { getChatHistory, getCurrentTherapist } from '../api/chat'
import { wsManager } from '../api/websocket'
import ChatHeader from '../components/chat/ChatHeader'
import MessageBubble from '../components/chat/MessageBubble'
import MessageSwipeWrapper from '../components/chat/MessageSwipeWrapper'
import AgentInsightBubble from '../components/chat/AgentInsightBubble'
import EmptyChat from '../components/chat/EmptyChat'
import ChatInput from '../components/chat/ChatInput'
import ProactivePrompt from '../components/chat/ProactivePrompt'
import ConsiliumBubble from '../components/chat/ConsiliumBubble'
import ConsiliumActionPlanCard from '../components/chat/ConsiliumActionPlanCard'
import TypingIndicator from '../components/chat/TypingIndicator'
import DateSeparator from '../components/chat/DateSeparator'
import SessionSeparator from '../components/chat/SessionSeparator'
import AdvancedDebugPanel from '../components/debug/AdvancedDebugPanel'
import { useInfiniteHistory } from '../hooks/useInfiniteHistory'

export default function ChatPage() {
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const sentinelRef = useRef<HTMLDivElement>(null)
  const shouldAutoScrollRef = useRef(true)

  const { messages, setMessages, addMessage, updateMessage, setSessionId, sessionId, setTherapist, currentTherapist, therapistStatus, isConnected, proactivePrompt, setProactivePrompt, debugEvents, addDebugEvent, consiliumActive, consiliumPhase, consiliumTurns, consiliumActionPlan, consiliumPrompt, setHasMoreHistory, addSessionMeta, sessionMeta } = useChatStore()
  const { loadOlderMessages, isLoadingHistory, hasMoreHistory } = useInfiniteHistory()

  // Derive current consilium speaker from last non-user turn
  const consiliumCurrentSpeaker = consiliumTurns.length > 0
    ? [...consiliumTurns].reverse().find(t => t.speaker !== 'user')?.speaker || null
    : null
  const { sendMessage, startVoiceRecording, sendVoiceChunk, endVoiceRecording } = useWebSocket()
  const { debugLevel, isDebugEnabled, isAdvancedDebug, setDebugLevel } = useDevMode()

  // Audio analyser for waveform visualization
  const audioAnalyser = useAudioAnalyser()

  // Voice recorder with analyser integration
  const voiceRecorder = useVoiceRecorder({
    onStreamReady: audioAnalyser.startAnalyser,
    onStreamEnd: audioAnalyser.stopAnalyser,
  })

  // Video recorder (same pattern as voice — tap to start/stop, auto-upload)
  const videoRecorder = useVideoRecorder()

  // Compute last user message index once (O(n) instead of O(n²) inside map)
  const lastUserMsgIdx = useMemo(
    () => messages.reduce((lastIdx, msg, idx) => (msg.role === 'user' ? idx : lastIdx), -1),
    [messages]
  )

  // Fetch initial chat history
  // Note: refetchOnWindowFocus disabled to prevent autoPlay re-triggering
  const { data: historyData } = useQuery({
    queryKey: ['chatHistory'],
    queryFn: () => getChatHistory(),
    staleTime: 30_000, // 30 seconds - prevent frequent refetches
    refetchOnWindowFocus: false, // Prevent autoPlay from re-triggering on tab switch
  })

  // Fetch current therapist
  const { data: therapistData } = useQuery({
    queryKey: ['currentTherapist'],
    queryFn: getCurrentTherapist,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  })

  // Check if onboarding is complete (from API data or localStorage)
  // API data takes priority — it syncs localStorage in getCurrentTherapist
  // Onboarding mode: only if BOTH API and localStorage say "not completed"
  // localStorage is set immediately on handoff event (before API refetch)
  const isOnboardingMode = !therapistData?.onboardingCompleted &&
    !localStorage.getItem('onboarding_complete')

  // Set messages from history
  useEffect(() => {
    if (historyData) {
      setMessages(historyData.messages)
      setSessionId(historyData.session_id)
      // Always start with true — even if current session is exhausted,
      // there may be older sessions. useInfiniteHistory will set false
      // when it confirms no previous sessions exist.
      setHasMoreHistory(true)
    }
  }, [historyData, setMessages, setSessionId, setHasMoreHistory])

  // Set therapist (extract only store-relevant fields) + session meta
  useEffect(() => {
    if (therapistData) {
      setTherapist({
        type: therapistData.type,
        name: therapistData.name,
        avatarUrl: therapistData.avatarUrl,
        voiceId: therapistData.voiceId,
      })
      if (sessionId) {
        addSessionMeta(sessionId, { therapist: therapistData.type, startedAt: new Date().toISOString() })
      }
    }
  }, [therapistData, setTherapist, sessionId, addSessionMeta])

  // "Jump to bottom" button visibility
  const [showScrollBtn, setShowScrollBtn] = useState(false)

  const scrollToBottom = useCallback((behavior: ScrollBehavior = 'smooth') => {
    messagesEndRef.current?.scrollIntoView({ behavior })
    shouldAutoScrollRef.current = true // keep auto-scrolling after programmatic scroll
  }, [])

  // Track scroll position for "↓" button + auto-scroll decision (B3 fix)
  useEffect(() => {
    const container = scrollContainerRef.current
    if (!container) return
    const handleScroll = () => {
      const distFromBottom = container.scrollHeight - container.scrollTop - container.clientHeight
      setShowScrollBtn(distFromBottom > 300)
      shouldAutoScrollRef.current = distFromBottom < 150
    }
    container.addEventListener('scroll', handleScroll, { passive: true })
    return () => container.removeEventListener('scroll', handleScroll)
  }, [])

  // Auto-scroll to bottom — uses pre-computed ref from scroll events (B3 fix)
  // Old approach checked isNearBottom AFTER DOM update, which fails for tall messages
  useEffect(() => {
    if (!scrollContainerRef.current || shouldAutoScrollRef.current) {
      requestAnimationFrame(() => scrollToBottom())
    }
  }, [messages, therapistStatus, consiliumTurns, consiliumActionPlan, consiliumPrompt, scrollToBottom])

  // IntersectionObserver for infinite scroll — load older messages on scroll to top
  useEffect(() => {
    const sentinel = sentinelRef.current
    const container = scrollContainerRef.current
    if (!sentinel || !container) return

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMoreHistory && !isLoadingHistory) {
          const prevScrollHeight = container.scrollHeight

          loadOlderMessages().then(() => {
            // Preserve scroll position after prepending
            requestAnimationFrame(() => {
              const newScrollHeight = container.scrollHeight
              container.scrollTop += (newScrollHeight - prevScrollHeight)
            })
          })
        }
      },
      { root: container, threshold: 0 }
    )

    observer.observe(sentinel)
    return () => observer.disconnect()
  }, [hasMoreHistory, isLoadingHistory, loadOlderMessages])

  // DEC-047/050: Sync debug level to backend when it changes or WebSocket connects
  useEffect(() => {
    if (isConnected) {
      wsManager.setDebugLevel(debugLevel)
    }
  }, [debugLevel, isConnected])

  // Load persisted debug events from DB when debug mode enabled
  useEffect(() => {
    if (!isDebugEnabled || !sessionId) return
    const apiUrl = import.meta.env.VITE_API_URL || ''
    fetch(`${apiUrl}/api/debug/events?session_id=${sessionId}`)
      .then((r) => r.ok ? r.json() : [])
      .then((events: Array<{ agent: string; icon: string; data: Record<string, unknown>; messageId: number; timestamp: string }>) => {
        for (const evt of events) {
          addDebugEvent({
            id: `db-${evt.agent}-${evt.messageId}-${evt.timestamp}`,
            agent: evt.agent as DebugEvent['agent'],
            icon: evt.icon,
            data: evt.data,
            messageId: evt.messageId,
            timestamp: evt.timestamp,
          })

          // DEC-058: Hydrate debugInfo on assistant messages from TechMetrics events
          if (evt.agent === 'TechMetrics') {
            // Find the assistant message that follows this user message
            const msgIdx = messages.findIndex(m => m.id === evt.messageId)
            if (msgIdx >= 0) {
              const nextAssistant = messages.slice(msgIdx + 1).find(m => m.role === 'assistant')
              if (nextAssistant && !nextAssistant.debugInfo) {
                updateMessage(nextAssistant.id, { debugInfo: evt.data as Message['debugInfo'] })
              }
            }
          }
        }
      })
      .catch(() => {/* ignore */})
  }, [isDebugEnabled, sessionId, addDebugEvent, messages, updateMessage])

  // Handle voice recording completion
  const voiceBlobUrlRef = useRef<string | null>(null)
  useEffect(() => {
    if (voiceRecorder.audioBlob) {
      // Revoke previous blob URL to prevent memory leak
      if (voiceBlobUrlRef.current) {
        URL.revokeObjectURL(voiceBlobUrlRef.current)
      }

      // Create blob URL for local playback
      const localVoiceUrl = URL.createObjectURL(voiceRecorder.audioBlob)
      voiceBlobUrlRef.current = localVoiceUrl

      // Create user voice message and add to chat
      const userVoiceMessage: Message = {
        id: nextTempId(),
        role: 'user',
        content: '', // Voice message has no text content
        mediaType: 'voice',
        status: 'sending',
        voiceUrl: localVoiceUrl,
        duration: voiceRecorder.duration, // Pass recorded duration for display
        createdAt: new Date().toISOString(),
      }

      // Add message to chat using store action (prevents infinite loop)
      addMessage(userVoiceMessage)
      setTimeout(() => scrollToBottom(), 50)

      // Send to server (capture duration before async)
      const recordedDuration = voiceRecorder.duration
      blobToBase64(voiceRecorder.audioBlob).then((base64) => {
        startVoiceRecording()
        sendVoiceChunk(base64)
        endVoiceRecording(recordedDuration)
      })
    }
  }, [voiceRecorder.audioBlob, voiceRecorder.duration, startVoiceRecording, sendVoiceChunk, endVoiceRecording, addMessage])

  // Handle video recording completion — auto-upload (same pattern as voice)
  const videoBlobUrlRef = useRef<string | null>(null)
  useEffect(() => {
    if (videoRecorder.videoBlob && videoRecorder.previewUrl) {
      // Revoke previous blob URL
      if (videoBlobUrlRef.current) {
        URL.revokeObjectURL(videoBlobUrlRef.current)
      }
      videoBlobUrlRef.current = videoRecorder.previewUrl

      // Create optimistic message
      const tempId = nextTempId()
      const optimisticMessage: Message = {
        id: tempId,
        role: 'user',
        content: '',
        mediaType: 'video',
        status: 'sending',
        videoUrl: videoRecorder.previewUrl,
        createdAt: new Date().toISOString(),
      }
      addMessage(optimisticMessage)
      setTimeout(() => scrollToBottom(), 50)

      // Upload via REST
      uploadVideo(videoRecorder.videoBlob).then((result) => {
        const messages = useChatStore.getState().messages
        const updatedMessages = messages.map((m) =>
          m.id === tempId
            ? {
                ...m,
                id: result.message_id,
                content: result.transcription || '',
                videoUrl: result.video_url || m.videoUrl,
                status: 'sent' as const,
              }
            : m
        )
        useChatStore.getState().setMessages(updatedMessages)
      }).catch((err) => {
        console.error('[VIDEO_UPLOAD] Failed:', err)
        // Remove optimistic message on failure
        const store = useChatStore.getState()
        store.setMessages(store.messages.filter((m) => m.id !== tempId))
      })
    }
  }, [videoRecorder.videoBlob, videoRecorder.previewUrl, addMessage])

  const handleSendMessage = (content: string, typingMetrics?: TypingMetrics) => {
    if (!content.trim()) return
    if (consiliumActive && (consiliumPhase === 'user_joining' || consiliumPhase === 'pre_synthesis')) {
      wsManager.sendConsiliumMessage(content.trim())
    } else {
      sendMessage(content.trim(), typingMetrics)
    }
    // Always scroll to bottom when user sends
    setTimeout(() => scrollToBottom(), 50)
  }

  const handleVoiceToggle = useCallback(() => {
    if (voiceRecorder.isRecording) {
      voiceRecorder.stopRecording()
    } else if (!voiceRecorder.isPending) {
      voiceRecorder.startRecording()
    }
  }, [voiceRecorder])

  const handleVoiceCancel = useCallback(() => {
    voiceRecorder.cancelRecording()
  }, [voiceRecorder])

  const handleVideoToggle = useCallback(() => {
    if (videoRecorder.isRecording) {
      videoRecorder.stopRecording()
    } else if (!videoRecorder.isPending) {
      videoRecorder.startRecording()
    }
  }, [videoRecorder])

  const handleVideoCancel = useCallback(() => {
    videoRecorder.cancelRecording()
  }, [videoRecorder])

  // Use currentTherapist from store (updated by WebSocket) or fall back to API data
  // In onboarding mode, force Finch/Harold as the therapist
  const baseTherapist = currentTherapist || therapistData
  const therapist = isOnboardingMode && !baseTherapist?.type?.includes('finch') && !baseTherapist?.type?.includes('harold')
    ? { type: 'finch', name: 'Гарольд', avatarUrl: '/avatars/finch.png' }
    : baseTherapist
  const therapistType = therapist?.type || 'wendy'

  // Background image per therapist
  const backgroundMap: Record<string, string> = {
    wendy: '/backgrounds/wendy_office.webp',
    artem: '/backgrounds/artem_office.webp',
    finch: '/backgrounds/harold_office.webp',
    harold: '/backgrounds/harold_office.webp',
    una: '/backgrounds/una_office.webp',
  }
  const backgroundImage = backgroundMap[therapistType] || backgroundMap.wendy

  return (
    <div
      className="flex flex-col h-screen mobile-fullscreen relative"
      style={{
        backgroundImage: `url(${backgroundImage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
    >
      {/* Dimming overlay - per-therapist: Una darkest (dusk), Artem lighter (show more bg) */}
      <div
        className={`absolute inset-0 backdrop-blur-[2px] ${
          therapistType === 'una' ? 'bg-white/30' :
          therapistType === 'artem' ? 'bg-white/40' :
          'bg-white/50'
        }`}
      />

      {/* Content wrapper - above overlay */}
      <div className="relative z-10 flex flex-col h-full">
        {/* Header */}
        <ChatHeader
          therapist={therapist}
          isConnected={isConnected}
          therapistStatus={therapistStatus}
          isOnboardingMode={isOnboardingMode}
          consiliumActive={consiliumActive}
          consiliumPhase={consiliumPhase}
          consiliumCurrentSpeaker={consiliumCurrentSpeaker}
        />

        {/* Consilium Phase Banner */}
        {consiliumActive && consiliumPhase && consiliumPhase !== 'completed' && (
          <div className="bg-finch/10 border-b border-finch/20 px-4 py-2 text-center text-sm">
            {consiliumPhase === 'gathering' && <span className="text-finch font-medium">Подготовка консилиума...</span>}
            {consiliumPhase === 'opening' && <span className="text-finch font-medium">Гарольд открывает консилиум</span>}
            {consiliumPhase === 'discussion' && <span className="text-neutral-600">Терапевты обсуждают ваш случай</span>}
            {consiliumPhase === 'user_joining' && <span className="text-primary-600 font-medium">Ваша очередь — задайте вопрос</span>}
            {consiliumPhase === 'pre_synthesis' && <span className="text-primary-600 font-medium">Гарольд хочет узнать ваше мнение</span>}
            {consiliumPhase === 'synthesis' && <span className="text-finch font-medium">Гарольд подводит итоги...</span>}
          </div>
        )}

        {/* Messages Area */}
        <div
          ref={scrollContainerRef}
          className="flex-1 overflow-y-auto scrollbar-gentle"
        >
          <div className="max-w-2xl mx-auto px-4 py-6 space-y-4">
            {/* Infinite scroll sentinel + loading indicator */}
            {hasMoreHistory && <div ref={sentinelRef} className="h-px" />}
            {isLoadingHistory && (
              <div className="flex justify-center py-4">
                <div className="w-6 h-6 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
              </div>
            )}

            {/* Empty State */}
            {messages.length === 0 && !isLoadingHistory && (
              <EmptyChat therapist={therapist} onPromptClick={handleSendMessage} />
            )}

            {/* Messages with debug bubbles + date/session separators */}
            {messages.map((message, index) => {
              const prevMessage = index > 0 ? messages[index - 1] : null

              // Session separator: when sessionId changes between messages
              const showSessionSeparator = prevMessage &&
                message.sessionId && prevMessage.sessionId &&
                message.sessionId !== prevMessage.sessionId

              // Date separator: when date changes (within same session)
              const msgDate = message.createdAt ? new Date(message.createdAt).toDateString() : ''
              const prevDate = prevMessage?.createdAt ? new Date(prevMessage.createdAt).toDateString() : ''
              const showDateSeparator = !showSessionSeparator && msgDate && prevDate && msgDate !== prevDate

              // Get debug events for this message (only for user messages)
              // DEC-052: single channel — all data from debugEvents, no cross-message lookup
              const messageDebugEvents = isDebugEnabled && message.role === 'user'
                ? debugEvents.filter((e: DebugEvent) => e.messageId === message.id)
                : []

              // Check if this is the last user message (for edit option)
              const isLastUserMessage = message.role === 'user' && index === lastUserMsgIdx

              // Get session meta for separator
              const prevSessionMeta = showSessionSeparator && prevMessage?.sessionId
                ? sessionMeta[prevMessage.sessionId]
                : null

              return (
                <div key={message.id}>
                  {showSessionSeparator && (
                    <SessionSeparator
                      therapist={prevSessionMeta?.therapist || 'wendy'}
                      date={prevSessionMeta?.startedAt || prevMessage!.createdAt}
                    />
                  )}
                  {showDateSeparator && (
                    <DateSeparator date={message.createdAt} />
                  )}
                  <MessageSwipeWrapper message={message} isLastUserMessage={isLastUserMessage}>
                    <MessageBubble
                      message={message}
                      therapist={therapist}
                      isLast={index === messages.length - 1 && !consiliumActive}
                    />
                  </MessageSwipeWrapper>
                  {/* DEC-052: Consolidated agent insight bubble after user message */}
                  {isDebugEnabled && messageDebugEvents.length > 0 && (
                    <AgentInsightBubble debugEvents={messageDebugEvents} />
                  )}
                </div>
              )
            })}

            {/* Interactive Consilium Turns */}
            {consiliumTurns.length > 0 && (
              <>
                {/* Divider */}
                <div className="flex items-center gap-3 my-4">
                  <div className="flex-1 h-px bg-neutral-300" />
                  <span className="text-xs text-neutral-500 font-medium">Консилиум</span>
                  <div className="flex-1 h-px bg-neutral-300" />
                </div>

                {/* Consilium turns */}
                {consiliumTurns.map((turn: ConsiliumTurn) => (
                  <ConsiliumBubble key={turn.id} turn={turn} />
                ))}
              </>
            )}

            {/* Consilium Ephemeral Prompt Card */}
            {consiliumPrompt && (
              <div className="mx-auto max-w-md animate-in fade-in slide-in-from-bottom-2">
                <div className="rounded-2xl border border-finch/20 bg-white/90 backdrop-blur-sm shadow-soft p-4 space-y-3">
                  {consiliumPrompt.message && (
                    <p className="text-sm text-neutral-700 text-center">{consiliumPrompt.message}</p>
                  )}
                  <div className="flex gap-2 justify-center">
                    {consiliumPrompt.buttons.map((btn) => (
                      <button
                        key={btn.action}
                        onClick={() => wsManager.sendConsiliumResponse(btn.action)}
                        className={`px-4 py-2.5 rounded-xl text-sm font-medium transition-colors min-h-[44px] ${
                          btn.action === 'join' || btn.action === 'respond'
                            ? 'bg-finch text-white hover:bg-finch/90'
                            : 'border border-neutral-300 text-neutral-600 hover:bg-neutral-50'
                        }`}
                      >
                        {btn.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Consilium Action Plan */}
            {consiliumActionPlan && (
              <ConsiliumActionPlanCard plan={consiliumActionPlan} />
            )}

            {/* Typing indicator bubble (Telegram-style, at bottom of messages) */}
            <TypingIndicator therapist={therapist} status={therapistStatus} />

            <div ref={messagesEndRef} className="h-1" />
          </div>
        </div>

        {/* Proactive Prompt */}
        {proactivePrompt && (
          <ProactivePrompt
            prompt={proactivePrompt}
            onRespond={(action, value) => {
              // Send proactive response via WebSocket
              sendMessage(`[proactive:${action}${value !== undefined ? ':' + value : ''}]`)
              setProactivePrompt(null)
            }}
            onDismiss={() => setProactivePrompt(null)}
            therapistType={therapistType}
          />
        )}

        {/* Jump to bottom button */}
        {showScrollBtn && (
          <div className="absolute bottom-24 right-4 z-20">
            <button
              onClick={() => scrollToBottom()}
              className="w-10 h-10 rounded-full bg-white shadow-lg border border-neutral-200 flex items-center justify-center text-neutral-500 hover:text-neutral-700 hover:shadow-xl transition-all"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 14l-7 7m0 0l-7-7m7 7V3" />
              </svg>
            </button>
          </div>
        )}

        {/* Input Area */}
        <div className="glass border-t border-neutral-200/50 safe-area-pb">
          <ChatInput
            onSendText={handleSendMessage}
            onVoiceToggle={handleVoiceToggle}
            onVoiceCancel={handleVoiceCancel}
            onVideoToggle={handleVideoToggle}
            onVideoCancel={handleVideoCancel}
            isRecording={voiceRecorder.isRecording}
            isPending={voiceRecorder.isPending}
            isVideoRecording={videoRecorder.isRecording}
            isVideoPending={videoRecorder.isPending}
            videoStreamRef={videoRecorder.streamRef}
            frequencyData={audioAnalyser.frequencyData}
            disabled={!isConnected || (consiliumActive && consiliumPhase !== 'user_joining' && consiliumPhase !== 'pre_synthesis' && consiliumPhase !== 'completed')}
            therapistType={therapistType}
            placeholder={consiliumActive && (consiliumPhase === 'user_joining' || consiliumPhase === 'pre_synthesis') ? 'Ваше сообщение для консилиума...' : undefined}
          />
        </div>
      </div>

      {/* Advanced Debug Panel (DEC-050) */}
      {isAdvancedDebug && (
        <AdvancedDebugPanel onClose={() => setDebugLevel('basic')} />
      )}
    </div>
  )
}
